import React, { useRef, useState } from "react";

import { ProductEntryDTO } from "@energy-advice/easas-api";
import { useDeleteFile } from "api/api-hooks/product-templates-hooks";
import { getUploadDownloadFileUrl } from "api/ProductTemplatesApi";
import {
  ITableDataEntry,
  LayoutConfig,
  MongoFile,
} from "components/dashboard/product-template-table/utils/interfaces";
import { errorToaster, successToaster } from "components/pages/measurement-devices/utils/utils";
import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { FileUpload, FileUploadUploadEvent } from "primereact/fileupload";
import { T } from "services/lang/LanguageService";
import { FileType } from "utils/enums";

type EntryFileProps = {
  entryData: ITableDataEntry;
  value: MongoFile;
  cellId: string;
  truncateWidth?: number;
  changeHandler?: (receivedValue: MongoFile | null) => void;
  isInEditMode: boolean;
  showThumbnail?: boolean;
  fileType?: FileType;
  dialog?: LayoutConfig;
};

const EntryFile: React.FC<EntryFileProps> = ({
  entryData,
  value,
  cellId,
  truncateWidth,
  changeHandler,
  isInEditMode,
  showThumbnail,
  fileType,
  dialog,
}) => {
  const [openDialog, setOpenDialog] = useState(false);
  const [isLoadingFile, setIsLoadingFile] = useState(true);

  const files = value?.files ?? [];
  const file = files[0];

  const getFileUrl = (type: "download" | "upload", fileKey?: string) => {
    let url = getUploadDownloadFileUrl(type, entryData.template, entryData.id!, cellId);
    if (type === "download") {
      url += `/${fileKey ?? file?.key}`;
    }
    return encodeURI(url);
  };

  const renderFileCell = () => {
    if (fileType === FileType.IMAGE && showThumbnail && file?.key) {
      return (
        <div onClick={() => setOpenDialog(true)} className="relative flex flex-column align-items-center">
          {isLoadingFile && <i className="absolute top-50 pi pi-spin pi-spinner" />}
          <img
            src={getFileUrl("download")}
            alt=""
            style={{ objectFit: "contain" }}
            width={showThumbnail ? 50 : 0}
            height={showThumbnail ? 50 : 0}
            onLoad={() => setIsLoadingFile(false)}
            onError={() => setIsLoadingFile(false)}
            loading="lazy"
          />
        </div>
      );
    }
    if (file?.key) {
      return (
        <span
          className="truncate px-1 underline text-blue-500 cursor-pointer"
          style={{ maxWidth: `${truncateWidth || 10}rem` }}
          onClick={() => setOpenDialog(true)}
        >
          {files.length > 1 ? `${files.length} ${T("side-menu.files")}` : file.fileName}
        </span>
      );
    }
    return isInEditMode ? (
      <Button icon="pi pi-folder" onClick={() => setOpenDialog(true)} text />
    ) : (
      <span>-</span>
    );
  };

  return (
    <div className="flex flex-column align-items-center">
      {renderFileCell()}
      <Dialog
        visible={openDialog}
        header={files.length > 1 ? `${files.length} files` : file?.fileName}
        onHide={() => setOpenDialog(false)}
        resizable
        style={{
          width: `min(100%, ${dialog?.width || 50}rem)`,
          height:
            !isInEditMode && fileType === FileType.IMAGE && file?.key
              ? `calc(${dialog?.height || 70}vh + 9.5rem)`
              : "auto",
        }}
      >
        <EntryFileDisplay
          entryData={entryData}
          cellId={cellId}
          changeHandler={changeHandler}
          isInEditMode={isInEditMode}
          value={value}
          fileType={fileType}
          getFileUrl={getFileUrl}
          dialog={dialog}
        />
      </Dialog>
    </div>
  );
};

export default EntryFile;

type EntryFileDisplayProps = {
  getFileUrl: (type: "download" | "upload", fileKey?: string) => string;
} & Pick<
  EntryFileProps,
  "entryData" | "cellId" | "changeHandler" | "isInEditMode" | "value" | "fileType" | "dialog"
>;

const EntryFileDisplay: React.FC<EntryFileDisplayProps> = ({
  entryData,
  cellId,
  changeHandler,
  isInEditMode,
  value,
  fileType,
  getFileUrl,
  dialog,
}) => {
  const [isLoadingFile, setIsLoadingFile] = useState(true);
  const uploadRef = useRef<FileUpload>(null);
  const files = value?.files ?? [];
  const file = files[0];

  const { mutate: deleteFile, isPending } = useDeleteFile({ safeName: entryData.template });

  const onUpload = (e: FileUploadUploadEvent) => {
    const response: ProductEntryDTO = JSON.parse(e.xhr.response);
    const responseValue = response.entries?.[cellId].value;
    if (responseValue) {
      changeHandler?.(responseValue);
      successToaster(T("alert.file-uploaded"));
    }
  };

  const handleRemoveFile = (fileKey: string) => {
    deleteFile(
      { entryId: entryData.id!, col: cellId, fileKey },
      {
        onSuccess: data => {
          const responseValue = data?.entries?.[cellId]?.value as MongoFile;
          changeHandler?.(responseValue ?? null);
        },
      }
    );
  };

  const getAcceptFile = () => {
    switch (fileType) {
      case FileType.IMAGE:
        return "image/*";
      case FileType.AUDIO:
        return "audio/*";
      case FileType.VIDEO:
        return "video/*";
      default:
        return "*/*";
    }
  };

  const handleIframeLoad = () => {
    setIsLoadingFile(false);
  };

  return (
    <div className="flex flex-column align-items-center">
      {isInEditMode && (
        <FileUpload
          ref={uploadRef}
          name="file"
          mode="advanced"
          url={getFileUrl("upload")}
          className="w-full"
          auto
          chooseOptions={{
            icon: "pi pi-upload",
          }}
          onUpload={onUpload}
          onError={e => {
            const resp = JSON.parse(e.xhr.response);
            errorToaster(resp.message);
            uploadRef.current?.clear;
          }}
          accept={getAcceptFile()}
        />
      )}
      {files.length > 0 && (
        <ul className="list-none p-0 m-0 mt-2 w-full">
          {files.map(f => (
            <li
              key={f.key}
              className="flex align-items-center justify-content-between py-1 px-4 border-1 border-300 surface-border border-round mb-2"
            >
              {f.fileName}
              {f.extension ? `.${f.extension}` : ""}
              <div>
                <Button
                  type="button"
                  onClick={() => window.open(`${getFileUrl("download", f.key)}?dl=true`)}
                  icon="pi pi-download"
                  className="p-button-rounded p-button-text p-button-success align-self-start"
                  tooltip={T("action.download")}
                  tooltipOptions={{ position: "left" }}
                />
                {isInEditMode && (
                  <Button
                    icon="pi pi-trash"
                    loading={isPending}
                    className="p-button-rounded p-button-danger p-button-text p-button-sm ml-2 flex-shrink-0"
                    onClick={() => handleRemoveFile(f.key)}
                    tooltip={T("action.delete")}
                    tooltipOptions={{ position: "left" }}
                  />
                )}
              </div>
            </li>
          ))}
        </ul>
      )}
      {!isInEditMode && file?.key && (
        <>
          {isLoadingFile && fileType !== FileType.OTHER && <i className="pi pi-spin pi-spinner mb-5" />}
          {fileType === FileType.AUDIO && (
            <audio
              controls
              onLoadedData={() => setIsLoadingFile(false)}
              onError={() => setIsLoadingFile(false)}
            >
              <source src={getFileUrl("download")} />
              <track kind="captions" />
            </audio>
          )}
          {fileType === FileType.VIDEO && (
            <video
              controls
              onLoadedData={() => setIsLoadingFile(false)}
              onError={() => setIsLoadingFile(false)}
              style={{ width: "100%", height: "auto" }}
            >
              <source src={getFileUrl("download")} />
              <track kind="captions" />
            </video>
          )}
          {fileType === FileType.IMAGE && (
            <img
              src={getFileUrl("download")}
              alt=""
              style={{ width: "100%" }}
              onLoad={() => setIsLoadingFile(false)}
              onError={() => setIsLoadingFile(false)}
            />
          )}
          {fileType === FileType.TEXT && (
            <iframe
              loading="lazy"
              src={getFileUrl("download")}
              title="file-view"
              onLoad={handleIframeLoad}
              style={{ maxHeight: "200vh", minHeight: `${dialog?.height || 70}vh` }}
              className="flex-1 border-none w-full"
            />
          )}
        </>
      )}
      {files.length === 0 && !isInEditMode && <div className="text-center">No file uploaded</div>}
    </div>
  );
};
